import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { AlertCircle } from "lucide-react";
import { useFinalizeSubscriptionMutation } from "../../api/subscriptionApi.ts";
// Use the "/pure" entry so Stripe.js is only injected when loadStripe() is
// actually called (at checkout), not eagerly on import. Keeps us within the
// TDDDG strictly-necessary cookie exemption — see privacy review.
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { Trans, useLingui } from "@lingui/react/macro";

const SubscriptionCheckout: React.FC = () => {
  const { t } = useLingui();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const stripePromise = useMemo(
    () =>
      loadStripe(
        "pk_test_51RvJN5EMcGEEF390QA3fvBFBIdZcfVddd6Trs1wvxPrJ42rHyUVyeu0lFuFkgglv3fmX3L9g5cgW07pzmUJL9UUK005rMJu1za",
      ),
    [],
  );
  const hasInitiatedFinalization = useRef(false);
  const [sessionSecret, setSessionSecret] = useState<string | null>(null);
  const [finalizePending, setFinalizePending] = useState(false);
  const [finalizeError, setFinalizeError] = useState(false);

  const { mutateAsync: finalizeSubscription } =
    useFinalizeSubscriptionMutation();

  useEffect(() => {
    if (subscriptionId && !hasInitiatedFinalization.current) {
      hasInitiatedFinalization.current = true;
      setFinalizePending(true);
      finalizeSubscription(subscriptionId)
        .then((data) => setSessionSecret(data.sessionSecret ?? null))
        .catch(() => setFinalizeError(true))
        .finally(() => setFinalizePending(false));
    }
  }, [subscriptionId, finalizeSubscription]);

  if (!subscriptionId) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="font-medium text-red-800">
              <Trans>
                Invalid subscription ID. Please check your link and try again.
              </Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (finalizePending) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-4 h-12 w-12">
              <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4"></div>
              <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent"></div>
            </div>
            <p className="text-gray-600">{t`Setting up payment...`}</p>
          </div>
        </div>
      </div>
    );
  }

  if (finalizeError) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">
                {t`Error setting up payment`}
              </p>
              <p className="mt-1 text-sm text-red-700">
                <Trans>
                  Please try again or contact support if the problem persists.
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!finalizePending && !finalizeError && !sessionSecret) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">
                {t`Error setting up payment`}
              </p>
              <p className="mt-1 text-sm text-red-700">
                <Trans>
                  Please try again or contact support if the problem persists.
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionSecret) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-4 h-12 w-12">
              <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4"></div>
              <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent"></div>
            </div>
            <p className="text-gray-600">
              <Trans>Preparing checkout</Trans>...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret: sessionSecret,
  };

  return (
    <div className="bg-white md:pt-2">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default SubscriptionCheckout;
